// Small WebP previews of community-report evidence photos.
//
// Originals are phone photos of 3-4 MB each, so a queue page that drew them as
// 48px thumbnails downloaded hundreds of megabytes. The report form now uploads
// a preview next to each original; the admin queue shows the preview and falls
// back to the original only when no preview exists yet.
//
// Key layout is mirrored by `reportThumbKey` in apps/worker/src/index.ts.

const THUMB_MAX_SIDE = 320;
const THUMB_QUALITY = 0.72;
export const REPORT_THUMB_MAX_BYTES = 200 * 1024;

const ORIGINAL_RE = /^(https:\/\/images\.checkcoa\.com\/reports\/)([a-z0-9-]+)\.[a-z0-9]+$/i;

/** The preview URL for an evidence photo, or null when the photo is not ours. */
export function reportThumbUrl(originalUrl: string): string | null {
    const match = ORIGINAL_RE.exec(originalUrl);
    return match ? `${match[1]}thumbs/${match[2]}.webp` : null;
}

/** Downscale a picked file to a WebP preview. Null when the browser cannot. */
export async function makeReportThumb(file: Blob): Promise<Blob | null> {
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, THUMB_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', THUMB_QUALITY));
        // Safari without WebP encoding returns PNG; the worker only stores WebP.
        if (!blob || blob.type !== 'image/webp' || blob.size > REPORT_THUMB_MAX_BYTES) return null;
        return blob;
    } catch {
        return null;
    }
}
